import { Injectable } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  docData,
  Firestore,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from '@angular/fire/firestore';
import { firstValueFrom, Observable } from 'rxjs';
import {
  ActivePlan,
  WorkoutPlan,
  ExerciseData,
  ExercisePlan,
  PlanHistory,
} from 'src/models/plan.model';
import { PlanDetailsPipe } from 'src/pipes/plan-details.pipe';
import { ErrorService } from './error.service';
import { ExerciseService } from './exercise-service.service';
import { MesocycleService } from './mesocycle.service';
import { toLocalDateKey } from 'src/utils/local-date';

@Injectable({
  providedIn: 'root',
})
export class PlanService {
  async getPlanHistoryById(id: string): Promise<PlanHistory> {
    try {
      const planHistoryDocRef = doc(this.firestore, `plansHistories/${id}`);
      const docSnap = await getDoc(planHistoryDocRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...(docSnap.data() as PlanHistory) };
      } else {
        throw new Error('Plan history not found');
      }
    } catch (error) {
      if (error instanceof Error) {
        this.errorService.showError(
          `Failed to get plan history: ${error.message}`
        );
      } else {
        this.errorService.showError(
          'Failed to get plan history due to an unexpected error'
        );
      }
      throw error; // Re-throw the error to be handled by the caller
    }
  }
  constructor(
    private firestore: Firestore,
    private auth: Auth,
    private planDetailPipe: PlanDetailsPipe,
    private errorService: ErrorService,
    private exerciseService: ExerciseService,
    private mesocycleService: MesocycleService
  ) {}

  async addWorkoutPlan(plan: WorkoutPlan): Promise<void> {
    try {
      const plansRef = collection(this.firestore, 'workoutPlans');
      await addDoc(plansRef, plan);
    } catch (error) {
      if (error instanceof Error) {
        // Use the error message from the caught error
        this.errorService.showError(
          `Failed to add workout plan: ${error.message}`
        );
      } else {
        // Fallback error message if the caught error is not an instance of Error
        this.errorService.showError(
          'Failed to add workout plan due to an unexpected error'
        );
      }
    }
  }

  async editWorkoutPlan(
    planId: string,
    planData: Partial<WorkoutPlan>
  ): Promise<void> {
    try {
      const planDocRef = doc(this.firestore, `workoutPlans/${planId}`);
      await updateDoc(planDocRef, planData);
      // Handle success, such as feedback to the user
    } catch (error) {
      // Handle error, such as showing an error message
    }
  }

  async removeWorkoutPlan(planId: string): Promise<void> {
    try {
      const user = await firstValueFrom(authState(this.auth));
      if (!user) throw new Error('No authenticated user found.');

      const activePlanRef = doc(this.firestore, `activePlans/${user.uid}`);
      const activePlanSnap = await getDoc(activePlanRef);

      if (
        activePlanSnap.exists() &&
        activePlanSnap.data()['activePlanId'] === planId
      ) {
        throw new Error('Cannot remove an active plan.');
      }

      const planDocRef = doc(this.firestore, `workoutPlans/${planId}`);
      await deleteDoc(planDocRef);
    } catch (error) {
      if (error instanceof Error) {
        // Use the error message from the caught error
        this.errorService.showError(
          `Failed to remove workout plan: ${error.message}`
        );
      } else {
        // Fallback error message if the caught error is not an instance of Error
        this.errorService.showError(
          'Failed to remove workout plan due to an unexpected error'
        );
      }
    }
  }

  getUserPlans(userId: string): Observable<WorkoutPlan[]> {
    return new Observable((observer) => {
      const plansRef = collection(this.firestore, 'workoutPlans');
      const q = query(plansRef, where('userId', '==', userId));

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          let plans: WorkoutPlan[] = [];
          snapshot.forEach((doc) => {
            const plan = { id: doc.id, ...(doc.data() as WorkoutPlan) };
            plans.push(plan);
          });

          // Perform local sorting by the `order` field or any other field
          plans = plans.sort((a, b) => {
            const orderA =
              a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
            const orderB =
              b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
          });

          observer.next(plans);
        },
        (error) => {
          this.errorService.showError(
            `Failed to fetch plans: ${error.message}`
          );
          observer.error(error);
        }
      );

      return { unsubscribe };
    });
  }

  async startPlan(userId: string, plan: WorkoutPlan): Promise<void> {
    try {
      const activePlanRef = doc(this.firestore, `activePlans/${userId}`);

      // Check if there's an existing active plan
      const docSnap = await getDoc(activePlanRef);
      if (docSnap.exists()) {
        throw new Error('User already has an active plan');
      }

      // Prepare the exerciseData based on plan.exercises
      const exerciseData = plan.exercises.reduce(
        (acc: ExerciseData, exercise) => {
          const weights = new Array(exercise.sets).fill(null); // Initialize weights array
          const reps = new Array(exercise.sets).fill(null); // Initialize reps array
          acc[exercise.id] = { weights, reps };
          return acc;
        },
        {} as ExerciseData
      );

      // Set or replace the active plan with the constructed exerciseData
      await setDoc(activePlanRef, {
        activePlanId: plan.id,
        startTime: new Date(),
        exerciseData,
      });
    } catch (error) {
      if (error instanceof Error) {
        // Utilize the error message from the caught error
        this.errorService.showError(`Failed to start plan: ${error.message}`);
      } else {
        // Generic error message if the error is not an instance of Error
        this.errorService.showError(
          'Failed to start plan due to an unexpected error'
        );
      }
    }
  }

  async endPlan(userId: string): Promise<void> {
    const activePlanRef = doc(this.firestore, `activePlans/${userId}`);
    await deleteDoc(activePlanRef);
  }

  getActivePlan(userId: string): Observable<any> {
    const activePlanRef = doc(this.firestore, `activePlans/${userId}`);
    return docData(activePlanRef, { idField: 'id' });
  }

  async updateActiveExerciseData(
    userId: string,
    exerciseData: { [key: string]: { weights: number[]; reps: number[] } }
  ): Promise<void> {
    try {
      const activePlanRef = doc(this.firestore, `activePlans/${userId}`);
      await setDoc(activePlanRef, { exerciseData }, { merge: true });
    } catch (error) {
      if (error instanceof Error) {
        // Use the error message from the caught exception
        this.errorService.showError(
          `Failed to update active exercise data: ${error.message}`
        );
      } else {
        // Generic error message for non-Error instances
        this.errorService.showError(
          'Failed to update active exercise data due to an unexpected error'
        );
      }
    }
  }

  async removeExerciseDataByKey(
    userId: string,
    keyToRemove: string
  ): Promise<void> {
    console.log(keyToRemove);
    try {
      const activePlanRef = doc(this.firestore, `activePlans/${userId}`);
      const updateData = {
        [`exerciseData.${keyToRemove}`]: deleteField(),
      };
      await updateDoc(activePlanRef, updateData);
    } catch (error) {
      if (error instanceof Error) {
        this.errorService.showError(
          `Failed to remove exercise data: ${error.message}`
        );
      } else {
        this.errorService.showError(
          'Failed to remove exercise data due to an unexpected error'
        );
      }
    }
  }

  async storePlanAndHistory(activePlan: ActivePlan, planName = 'New plan') {
    try {
      const user = await firstValueFrom(authState(this.auth));
      if (!user) throw new Error('No authenticated user found.');

      // A workout is permanently associated using its start date, not its
      // finish date. A lookup error aborts the whole operation; only a
      // successful "no match" lookup produces an unassigned history.
      const workoutStartDate = toLocalDateKey(activePlan.startTime);
      const mesocycle = await this.mesocycleService.getActiveForDate(
        user.uid,
        workoutStartDate
      );
      if (mesocycle && !mesocycle.id) {
        throw new Error('Active mesocycle has no document id.');
      }
      const mesocycleId = mesocycle?.id;
      const mesocycleFields = mesocycleId ? { mesocycleId } : {};

      if (activePlan.activePlanId !== '') {
        const workoutPlanRef = doc(
          this.firestore,
          'workoutPlans',
          activePlan.activePlanId
        );
        const workoutPlanSnapshot = await getDoc(workoutPlanRef);
        if (!workoutPlanSnapshot.exists())
          throw new Error('No such workout plan document!');

        const workoutPlanData = workoutPlanSnapshot.data();
        planName = workoutPlanData['planName'];
      }

      for (const exerciseId of Object.keys(activePlan.exerciseData)) {
        const exerciseRef = doc(this.firestore, 'exercises', exerciseId);
        const exerciseSnapshot = await getDoc(exerciseRef);
        if (exerciseSnapshot.exists()) {
          const exerciseName = exerciseSnapshot.data()['sportName'];
          activePlan.exerciseData[exerciseId].name = exerciseName;
        }
      }

      // Filter out exercises that are disabled or have a swap field
      const filteredExerciseData = Object.fromEntries(
        Object.entries(activePlan.exerciseData).filter(
          ([_, exerciseData]) =>
            exerciseData.swap === undefined ||
            (exerciseData.swap === '' && exerciseData.disabled !== false)
        )
      );

      // Update the activePlan with the filtered data
      activePlan.exerciseData = filteredExerciseData;

      const finishTime = new Date();
      const plansCollectionRef = collection(this.firestore, 'plansHistories');
      const planRef = doc(plansCollectionRef);
      const batch = writeBatch(this.firestore);
      batch.set(planRef, {
        ...activePlan,
        planName,
        userId: user.uid,
        finishTime,
        ...mesocycleFields,
      });

      const exerciseHistoriesCollectionRef = collection(
        this.firestore,
        'exerciseHistories'
      );
      Object.entries(filteredExerciseData).forEach(
        ([exerciseId, exerciseData]) => {
          let highestWeight = 0;
          let highestRep = 0;

          exerciseData.weights.forEach((weight, index) => {
            if (
              weight > highestWeight ||
              (weight === highestWeight &&
                exerciseData.reps[index] > highestRep)
            ) {
              highestWeight = weight;
              highestRep = exerciseData.reps[index];
            }
          });

          const history = {
            highestWeight: highestWeight,
            highestRep: highestRep,
            planId: planRef.id,
            userId: user.uid,
            exerciseId,
            ...exerciseData,
            createdAt: finishTime,
            activePlanId: activePlan.activePlanId,
            ...mesocycleFields,
          };

          batch.set(doc(exerciseHistoriesCollectionRef), history);
        }
      );

      // Saving history and clearing the active workout succeed or fail as one
      // unit. A failed commit therefore remains safely retryable, while a
      // successful commit cannot leave an active workout that can be saved
      // again as duplicate history.
      batch.delete(doc(this.firestore, `activePlans/${user.uid}`));
      await batch.commit();
      return { planHistoryId: planRef.id, mesocycleId };
    } catch (error) {
      if (error instanceof Error) {
        this.errorService.showError(
          `Failed to store plan and history: ${error.message}`
        );
      } else {
        this.errorService.showError(
          'Failed to store plan and history due to an unexpected error'
        );
      }
      // Callers must be able to retain the active plan and offer a retry.
      throw error;
    }
  }

  async removeActivePlan(): Promise<void> {
    try {
      const user = await firstValueFrom(authState(this.auth));
      if (!user) throw new Error('No authenticated user found.');

      const activePlanRef = doc(this.firestore, `activePlans/${user.uid}`);
      await deleteDoc(activePlanRef);
    } catch (error) {
      if (error instanceof Error) {
        // Utilize the error message from the caught exception
        this.errorService.showError(
          `Failed to remove active plan: ${error.message}`
        );
      } else {
        // Generic error message for non-Error instances
        this.errorService.showError(
          'Failed to remove active plan due to an unexpected error'
        );
      }
      throw error;
    }
  }

  async addExtraPlanToActive(extraPlan: ExercisePlan): Promise<void> {
    try {
      const user = await firstValueFrom(authState(this.auth));
      if (!user) throw new Error('No authenticated user found.');

      const activePlanRef = doc(this.firestore, `activePlans/${user.uid}`);
      await updateDoc(activePlanRef, {
        extraPlans: arrayUnion(extraPlan),
      });
    } catch (error) {
      if (error instanceof Error) {
        // Utilize the error message from the caught exception
        this.errorService.showError(
          `Error adding extra plan: ${error.message}`
        );
      } else {
        // Generic error message for non-Error instances
        this.errorService.showError(
          'Error adding extra plan due to an unexpected error'
        );
      }
    }
  }

  private filterDisabled(dataObject: ExerciseData, dataArray: ExercisePlan[]) {
    // Filter out IDs that are marked as disabled in the dataObject
    const enabledIds = Object.keys(dataObject).filter(
      (key) => !dataObject[key].disabled
    );

    // Use the filtered IDs to get the corresponding items from the dataArray
    const filteredArray = dataArray.filter((item) =>
      enabledIds.includes(item.id)
    );

    return filteredArray;
  }

  async storeExtraPlan(
    activePlan: ActivePlan,
    newProperties: {
      defaultSets: number;
      planName: string;
      planDescription: string;
    },
    filterDisabled: boolean = false
  ): Promise<void> {
    try {
      const user = await firstValueFrom(authState(this.auth));
      if (!user) throw new Error('No authenticated user found.');

      const planObservable = this.planDetailPipe.transform(
        activePlan.activePlanId
      );
      if (!planObservable) throw new Error('No plan observable found.');

      const oldPlan = await firstValueFrom(planObservable);
      if (!oldPlan) throw new Error('No plan found.');

      if (!oldPlan.userId) oldPlan.userId = user.uid;

      const extraExercises = activePlan.extraPlans || [];
      const combinedExercises = [...oldPlan.exercises, ...extraExercises];
      const filteredExercisePlans = filterDisabled
        ? this.filterDisabled(activePlan.exerciseData, combinedExercises)
        : combinedExercises;

      const newPlan: WorkoutPlan = {
        ...oldPlan,
        ...newProperties,
        exercises: filteredExercisePlans,
      };

      delete newPlan.id; // Remove the old plan's ID to prevent overwriting

      const plansRef = collection(this.firestore, 'workoutPlans');
      await addDoc(plansRef, newPlan);
    } catch (error) {
      if (error instanceof Error) {
        // Use the error message from the caught exception for detailed feedback
        console.error(`Failed to store extra plan: ${error.message}`);
        this.errorService.showError(
          `Failed to store extra plan: ${error.message}`
        );
      } else {
        // Fallback error handling for non-Error instances
        this.errorService.showError(
          'Failed to store extra plan due to an unexpected error'
        );
      }
    }
  }

  async getUserPlanHistories(): Promise<PlanHistory[]> {
    try {
      const user = await firstValueFrom(authState(this.auth));
      if (!user) throw new Error('No authenticated user found.');

      const plansHistoryRef = collection(this.firestore, 'plansHistories');
      const q = query(
        plansHistoryRef,
        where('userId', '==', user.uid),
        orderBy('finishTime', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const planHistories: PlanHistory[] = [];
      querySnapshot.forEach((doc) => {
        const planHistory = { id: doc.id, ...(doc.data() as PlanHistory) };
        planHistory.id = doc.id;
        planHistories.push(planHistory);
      });

      return planHistories;
    } catch (error) {
      if (error instanceof Error) {
        this.errorService.showError(
          `Failed to fetch user plan histories: ${error.message}`
        );
      } else {
        this.errorService.showError(
          'Failed to fetch user plan histories due to an unexpected error'
        );
      }
      return []; // Return an empty array in case of error to maintain function return type consistency
    }
  }

  async removePlanHistoryById(planHistoryId: string): Promise<void> {
    try {
      const planHistoryRef = doc(
        this.firestore,
        `plansHistories/${planHistoryId}`
      );
      await deleteDoc(planHistoryRef);
    } catch (error) {
      if (error instanceof Error) {
        this.errorService.showError(
          `Failed to remove plan history: ${error.message}`
        );
      } else {
        this.errorService.showError(
          'Failed to remove plan history due to an unexpected error'
        );
      }
      // Consider how you want to handle the error. Re-throwing might be suitable in some cases.
    }
  }

  async sharePlan(plan: WorkoutPlan): Promise<void> {
    try {
      const user = await firstValueFrom(authState(this.auth));
      if (!user) throw new Error('No authenticated user found.');

      const exercisesWithInfo = await Promise.all(
        plan.exercises.map(async (exercise) => {
          const exerciseInfo = await this.exerciseService.getExercise(
            exercise.id
          );
          return {
            ...exercise,
            info: exerciseInfo || {}, // Add additional info if available
          };
        })
      );

      const planWithInfo = {
        ...plan,
        exercises: exercisesWithInfo,
        userId: user.uid, // Optionally add user ID to the plan data
      };

      const sharedPlansRef = collection(this.firestore, 'sharedPlans');
      const planDocRef = doc(sharedPlansRef, plan.id);
      await setDoc(planDocRef, planWithInfo);

      // Copy the URL to the clipboard
      if (plan.id) this.copyToClipboard(plan.id);
    } catch (error) {
      if (error instanceof Error) {
        this.errorService.showError(`Failed to share plan: ${error.message}`);
      } else {
        this.errorService.showError(
          'Failed to share plan due to an unexpected error'
        );
      }
    }
  }

  public async copyToClipboard(planId: string) {
    // Generate the URL to be shared
    const currentUrl = window.location.origin;
    const shareUrl = `${currentUrl}/share/${planId}`;

    try {
      await navigator.clipboard
        .writeText(shareUrl)
        .then(() => {})
        .catch((err) => {
          console.error('Failed to copy: ', err);
        });
    } catch (err) {
      throw new Error('Failed to copy URL');
    }
  }

  async getSharedPlan(planId: string): Promise<WorkoutPlan | undefined> {
    try {
      const planDocRef = doc(this.firestore, `sharedPlans/${planId}`);
      const docSnap = await getDoc(planDocRef);
      if (docSnap.exists()) {
        return docSnap.data() as WorkoutPlan;
      } else {
        throw new Error('No such plan!');
      }
    } catch (error) {
      if (error instanceof Error) {
        this.errorService.showError(`Failed to get plan: ${error.message}`);
      } else {
        this.errorService.showError(
          'Failed to get plan due to an unexpected error'
        );
      }
      return undefined;
    }
  }

  async getWorkoutPlanById(planId: string): Promise<WorkoutPlan | null> {
    try {
      const planDocRef = doc(this.firestore, `workoutPlans/${planId}`);
      const docSnap = await getDoc(planDocRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...(docSnap.data() as WorkoutPlan) };
      } else {
        return null;
      }
    } catch (error) {
      this.errorService.showError(`Failed to get workout plan: ${error}`);
      return null;
    }
  }

  async getPlanHistoriesByPlanId(planId: string): Promise<PlanHistory[]> {
    try {
      const currentUserId = this.auth.currentUser?.uid;
      if (!currentUserId) {
        throw new Error('User not authenticated');
      }

      const plansHistoryRef = collection(this.firestore, 'plansHistories');
      const q = query(
        plansHistoryRef,
        where('activePlanId', '==', planId),
        where('userId', '==', currentUserId),
        orderBy('finishTime', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const planHistories: PlanHistory[] = [];
      querySnapshot.forEach((doc) => {
        const planHistory = { id: doc.id, ...(doc.data() as PlanHistory) };
        planHistories.push(planHistory);
      });

      return planHistories;
    } catch (error) {
      this.errorService.showError(`Failed to fetch plan histories: ${error}`);
      return [];
    }
  }

  async updateWorkoutPlansOrder(userId: string, updatedPlans: WorkoutPlan[]) {
    try {
      const batch = writeBatch(this.firestore); // Create a batch instance

      updatedPlans.forEach((plan) => {
        const planDocRef = doc(this.firestore, `workoutPlans/${plan.id}`);
        batch.update(planDocRef, { order: plan.order }); // Add each update to the batch
      });

      await batch.commit(); // Commit the batch
    } catch (error) {
      if (error instanceof Error) {
        this.errorService.showError(
          `Failed to update workout plan order: ${error.message}`
        );
      } else {
        this.errorService.showError(
          'Failed to update workout plan order due to an unexpected error'
        );
      }
    }
  }

  async getPlanNamesByIds(planIds: string[]): Promise<Map<string, string>> {
    try {
      const planNamesMap = new Map<string, string>();
      // A history created from "no plan" stores an empty activePlanId.  Old
      // malformed values must be ignored instead of being used to construct
      // an invalid Firestore document reference.
      const validPlanIds = [...new Set(planIds)].filter(
        (planId) => typeof planId === 'string' && planId.trim() !== '' && !planId.includes('/')
      );

      // Fetch plan names for each ID
      const fetchPromises = validPlanIds.map(async (planId) => {
        const planDocRef = doc(this.firestore, 'workoutPlans', planId);
        const docSnap = await getDoc(planDocRef);

        if (docSnap.exists()) {
          const planName = docSnap.data()['planName'];
          planNamesMap.set(planId, planName);
        }
      });

      await Promise.all(fetchPromises);
      return planNamesMap;
    } catch (error) {
      if (error instanceof Error) {
        this.errorService.showError(
          `Failed to get plan names: ${error.message}`
        );
      } else {
        this.errorService.showError(
          'Failed to get plan names due to an unexpected error'
        );
      }
      return new Map(); // Return an empty map in case of an error
    }
  }
}
